/**
 * Supabase repository — typed wrapper around the Supabase client.
 *
 * This is the ONLY place in the codebase that knows about the camelCase ↔
 * snake_case mapping between TypeScript types and Postgres columns.
 *
 * Not a StorageAdapter. Called by sync-service.ts and user-service.ts (billing-tier refresh).
 */

import { supabase } from './supabase';

import type { User, UserPreferences, Tier, SubscriptionStatus } from '@/types/user';
import type { Property } from '@/types/property';
import type { Waypoint } from '@/types/waypoint';
import type { Season } from '@/types/season';
import type { Task } from '@/types/task';
import type { LogEntry } from '@/types/log-entry';
import type { ShoppingListItem } from '@/types/shopping-list';
import type { Hunt, HuntSit, HarvestRecord } from '@/types/hunt';
import type { HuntJournalEntry } from '@/types/hunt-journal-entry';
import type { PhotoRecord } from '@/types/photo';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(id: string): boolean {
  return UUID_REGEX.test(id);
}

function throwOnError(error: unknown, context: string): never {
  let msg: string;
  if (error instanceof Error) {
    msg = error.message;
  } else if (error !== null && typeof error === 'object') {
    // Supabase client errors are plain objects with message/code/hint/details fields
    const e = error as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof e['message'] === 'string') parts.push(e['message']);
    if (e['code'] !== undefined) parts.push(`code: ${String(e['code'])}`);
    if (typeof e['hint'] === 'string' && e['hint']) parts.push(`hint: ${e['hint']}`);
    if (typeof e['details'] === 'string' && e['details']) parts.push(`details: ${e['details']}`);
    msg = parts.length > 0 ? parts.join(', ') : JSON.stringify(error);
  } else {
    msg = String(error);
  }
  throw new Error(`[supabase-repository] ${context}: ${msg}`);
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

// Billing fields (tier, stripeCustomerId, subscriptionStatus) are server-authoritative.
// They are intentionally excluded — written only by Edge Functions via service role key. ADR-032.
function toSupabaseUser(u: User): Record<string, unknown> {
  return {
    id: u.id,
    email: u.email,
    display_name: u.displayName,
    default_state: u.defaultState,
    preferred_units: u.preferredUnits,
    created_at: u.createdAt,
    updated_at: u.updatedAt,
  };
}

function fromSupabaseUser(row: Record<string, unknown>): User {
  return {
    id: row['id'] as string,
    email: row['email'] as string,
    displayName: (row['display_name'] as string) ?? '',
    defaultState: (row['default_state'] as string) ?? '',
    preferredUnits: ((row['preferred_units'] as string) ?? 'imperial') as 'imperial' | 'metric',
    tier: ((row['tier'] as string) ?? 'starter') as Tier,
    stripeCustomerId: (row['stripe_customer_id'] as string | null) ?? null,
    subscriptionStatus: (row['subscription_status'] as SubscriptionStatus) ?? null,
    betaExpiresAt: (row['beta_expires_at'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertUser(user: User): Promise<void> {
  if (!isUuid(user.id)) {
    console.warn(`[supabase-repository] skipping upsertUser: non-UUID id "${user.id}"`);
    return;
  }
  const { error } = await supabase.from('users').upsert(toSupabaseUser(user), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertUser');
}

export async function fetchUserSince(since: string): Promise<User | null> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .gt('updated_at', since)
    .maybeSingle();
  if (error) throwOnError(error, 'fetchUserSince');
  return data ? fromSupabaseUser(data as Record<string, unknown>) : null;
}

/**
 * Fetch the current authenticated user's row from Supabase (no `since` filter).
 * Used by refreshSubscription to hydrate server-authoritative billing fields
 * (tier, subscriptionStatus) into the local Dexie record after Stripe checkout.
 */
export async function fetchCurrentUserById(userId: string): Promise<User | null> {
  if (!isUuid(userId)) return null;
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) throwOnError(error, 'fetchCurrentUserById');
  return data ? fromSupabaseUser(data as Record<string, unknown>) : null;
}

export async function softDeleteUser(id: string): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteUser');
}

// ---------------------------------------------------------------------------
// User Preferences
// ---------------------------------------------------------------------------

function toSupabasePreferences(p: UserPreferences): Record<string, unknown> {
  // M-07: defaultPropertyId removed; primary property lives on Property.isPrimary now.
  return {
    id: p.id,
    user_id: p.userId,
    theme: p.theme,
    notifications_enabled: p.notificationsEnabled,
    notification_lead_days: p.notificationLeadDays,
    updated_at: p.updatedAt,
  };
}

function fromSupabasePreferences(row: Record<string, unknown>): UserPreferences {
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    theme: ((row['theme'] as string) ?? 'system') as 'light' | 'dark' | 'system',
    notificationsEnabled: (row['notifications_enabled'] as boolean) ?? false,
    notificationLeadDays: (row['notification_lead_days'] as number) ?? 3,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertUserPreferences(prefs: UserPreferences): Promise<void> {
  const { error } = await supabase
    .from('user_preferences')
    .upsert(toSupabasePreferences(prefs), { onConflict: 'user_id' });
  if (error) throwOnError(error, 'upsertUserPreferences');
}

export async function fetchUserPreferencesSince(since: string): Promise<UserPreferences | null> {
  const { data, error } = await supabase
    .from('user_preferences')
    .select('*')
    .gt('updated_at', since)
    .maybeSingle();
  if (error) throwOnError(error, 'fetchUserPreferencesSince');
  return data ? fromSupabasePreferences(data as Record<string, unknown>) : null;
}

export async function softDeleteUserPreferences(id: string): Promise<void> {
  const { error } = await supabase
    .from('user_preferences')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteUserPreferences');
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

function toSupabaseProperty(p: Property): Record<string, unknown> {
  return {
    id: p.id,
    user_id: p.userId,
    name: p.name,
    latitude: p.latitude,
    longitude: p.longitude,
    state: p.state,
    zone: p.zone,
    notes: p.notes,
    // Coerce undefined → null: Postgres rejects undefined values
    gmu_status: p.gmuStatus ?? null,
    gmu: p.gmu ?? null,
    // M-07: primary-property flag mirrors Season.isPrimary; column added in
    // supabase migration 0016. Default to false on the wire so the column is
    // never null.
    is_primary: p.isPrimary ?? false,
    created_at: p.createdAt,
    updated_at: p.updatedAt,
  };
}

function fromSupabaseProperty(row: Record<string, unknown>): Property {
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    name: row['name'] as string,
    latitude: row['latitude'] as number,
    longitude: row['longitude'] as number,
    state: (row['state'] as string) ?? '',
    zone: (row['zone'] as string) ?? '',
    notes: (row['notes'] as string) ?? '',
    // CAST: JSONB from Postgres is safe to cast to ResolvedGmu | null
    gmu: (row['gmu'] as Property['gmu']) ?? null,
    // CAST: gmu_status is a TEXT column matching GmuResolutionStatus values; validated at write time
    gmuStatus: (row['gmu_status'] as Property['gmuStatus']) ?? undefined,
    // M-07: is_primary is BOOLEAN NOT NULL DEFAULT false in Postgres
    isPrimary: (row['is_primary'] as boolean) ?? false,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertProperty(property: Property): Promise<void> {
  const { error } = await supabase
    .from('properties')
    .upsert(toSupabaseProperty(property), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertProperty');
}

export async function fetchPropertiesSince(since: string): Promise<Property[]> {
  const { data, error } = await supabase
    .from('properties')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchPropertiesSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseProperty);
}

export async function softDeleteProperty(id: string): Promise<void> {
  const { error } = await supabase
    .from('properties')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteProperty');
}

// ---------------------------------------------------------------------------
// Waypoints
// ---------------------------------------------------------------------------

function toSupabaseWaypoint(w: Waypoint): Record<string, unknown> {
  return {
    id: w.id,
    property_id: w.propertyId,
    name: w.name,
    type: w.type,
    latitude: w.latitude,
    longitude: w.longitude,
    notes: w.notes,
    metadata: w.metadata ?? {},
    created_at: w.createdAt,
    updated_at: w.updatedAt,
  };
}

function fromSupabaseWaypoint(row: Record<string, unknown>): Waypoint {
  return {
    id: row['id'] as string,
    propertyId: row['property_id'] as string,
    name: row['name'] as string,
    type: row['type'] as Waypoint['type'],
    latitude: row['latitude'] as number,
    longitude: row['longitude'] as number,
    notes: (row['notes'] as string) ?? '',
    // CAST: JSONB from Postgres is safe to cast to WaypointMetadata
    metadata: row['metadata'] as Waypoint['metadata'],
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertWaypoint(waypoint: Waypoint): Promise<void> {
  const { error } = await supabase
    .from('waypoints')
    .upsert(toSupabaseWaypoint(waypoint), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertWaypoint');
}

export async function fetchWaypointsSince(since: string): Promise<Waypoint[]> {
  const { data, error } = await supabase
    .from('waypoints')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchWaypointsSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseWaypoint);
}

export async function softDeleteWaypoint(id: string): Promise<void> {
  const { error } = await supabase
    .from('waypoints')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteWaypoint');
}

// ---------------------------------------------------------------------------
// Seasons
// ---------------------------------------------------------------------------

function toSupabaseSeason(s: Season): Record<string, unknown> {
  return {
    id: s.id,
    user_id: s.userId,
    name: s.name,
    state: s.state,
    weapon_type: s.seasonType,
    opener_date: s.openerDate,
    closer_date: s.closerDate ?? null,
    created_at: s.createdAt,
    updated_at: s.updatedAt,
  };
}

function fromSupabaseSeason(row: Record<string, unknown>): Season {
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    name: row['name'] as string,
    state: (row['state'] as string) ?? '',
    seasonType: row['weapon_type'] as Season['seasonType'],
    openerDate: row['opener_date'] as string,
    closerDate: (row['closer_date'] as string | null) ?? undefined,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertSeason(season: Season): Promise<void> {
  const { error } = await supabase
    .from('seasons')
    .upsert(toSupabaseSeason(season), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertSeason');
}

export async function fetchSeasonsSince(since: string): Promise<Season[]> {
  const { data, error } = await supabase
    .from('seasons')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchSeasonsSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseSeason);
}

export async function softDeleteSeason(id: string): Promise<void> {
  const { error } = await supabase
    .from('seasons')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteSeason');
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

function toSupabaseTask(t: Task): Record<string, unknown> {
  return {
    id: t.id,
    template_task_id: (t.templateTaskId && isUuid(t.templateTaskId)) ? t.templateTaskId : null,
    template_id: (t.templateId && isUuid(t.templateId)) ? t.templateId : null,
    season_id: t.seasonId,
    property_id: t.propertyId ?? null,
    waypoint_id: t.waypointId ?? null,
    title: t.title,
    description: t.description,
    category: t.category ?? 'other',
    selected_action: t.selectedAction ?? '',
    due_date: t.dueDate,
    timing_anchor: t.timingAnchor ?? 'opener_relative',
    offset_days: t.offsetDays,
    depends_on: t.dependsOn ?? [],
    blocked: t.blocked ?? false,
    completed: t.completed,
    completed_at: t.completedAt ?? null,
    snoozed_until: t.snoozedUntil ?? null,
    sort_order: t.sortOrder,
    notes: t.notes ?? '',
    created_at: t.createdAt,
    updated_at: t.updatedAt,
  };
}

function fromSupabaseTask(row: Record<string, unknown>): Task {
  return {
    id: row['id'] as string,
    templateTaskId: (row['template_task_id'] as string | null) ?? null,
    templateId: (row['template_id'] as string | null) ?? null,
    seasonId: row['season_id'] as string,
    propertyId: (row['property_id'] as string | null) ?? undefined,
    waypointId: (row['waypoint_id'] as string | null) ?? undefined,
    title: row['title'] as string,
    description: (row['description'] as string) ?? '',
    category: row['category'] as Task['category'],
    selectedAction: (row['selected_action'] as string) ?? '',
    dueDate: row['due_date'] as string,
    timingAnchor: row['timing_anchor'] as Task['timingAnchor'],
    offsetDays: (row['offset_days'] as number) ?? 0,
    dependsOn: (row['depends_on'] as string[]) ?? [],
    blocked: (row['blocked'] as boolean) ?? false,
    completed: (row['completed'] as boolean) ?? false,
    completedAt: (row['completed_at'] as string | null) ?? undefined,
    snoozedUntil: (row['snoozed_until'] as string | null) ?? undefined,
    sortOrder: (row['sort_order'] as number) ?? 0,
    notes: (row['notes'] as string) ?? '',
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertTask(task: Task): Promise<void> {
  if (!isUuid(task.id)) {
    console.warn(`[supabase-repository] skipping upsertTask: non-UUID id "${task.id}"`);
    return;
  }
  const { error } = await supabase
    .from('tasks')
    .upsert(toSupabaseTask(task), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertTask');
}

export async function fetchTasksSince(since: string): Promise<Task[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchTasksSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseTask);
}

export async function softDeleteTask(id: string): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteTask');
}

// ---------------------------------------------------------------------------
// Log Entries
// ---------------------------------------------------------------------------

function toSupabaseLogEntry(e: LogEntry): Record<string, unknown> {
  return {
    id: e.id,
    waypoint_id: e.waypointId,
    property_id: e.propertyId ?? '',
    date: e.date,
    time: e.time ?? '',
    notes: e.notes,
    tags: e.tags,
    weather_temp: e.weatherTemp ?? null,
    weather_condition: e.weatherCondition ?? null,
    weather_precipitation: e.weatherPrecipitation ?? null,
    wind_direction: e.windDirection ?? null,
    wind_speed: e.windSpeed ?? null,
    moon_phase: e.moonPhase ?? null,
    moon_illumination: e.moonIllumination ?? null,
    barometric_pressure: e.barometricPressure ?? null,
    barometric_trend: e.barometricTrend ?? null,
    weather_feels_like: e.weatherFeelsLike ?? null,
    weather_humidity: e.weatherHumidity ?? null,
    weather_icon: e.weatherIcon ?? null,
    civil_dawn: e.civilDawnTime ?? null,
    civil_dusk: e.civilDuskTime ?? null,
    sun_rise: e.sunriseTime ?? null,
    sun_set: e.sunsetTime ?? null,
    animal_observed: e.animalObserved ?? false,
    animal_species: e.animalSpecies ?? null,
    animal_sex: e.animalSex ?? null,
    animal_age_class: e.animalAgeClass ?? null,
    animal_antler_description: e.animalAntlerDescription ?? null,
    animal_count: e.animalCount ?? null,
    animal_behavior: e.animalBehavior ?? null,
    photos: e.photos ?? [],
    created_at: e.createdAt,
    updated_at: e.updatedAt,
  };
}

function fromSupabaseLogEntry(row: Record<string, unknown>): LogEntry {
  return {
    id: row['id'] as string,
    waypointId: row['waypoint_id'] as string,
    propertyId: (row['property_id'] as string) || undefined,
    date: row['date'] as string,
    time: (row['time'] as string) || undefined,
    notes: (row['notes'] as string) ?? '',
    tags: (row['tags'] as string[]) ?? [],
    weatherTemp: (row['weather_temp'] as number | null) ?? undefined,
    weatherCondition: (row['weather_condition'] as string | null) ?? undefined,
    weatherPrecipitation: (row['weather_precipitation'] as string | null) ?? undefined,
    windDirection: (row['wind_direction'] as string | null) ?? undefined,
    windSpeed: (row['wind_speed'] as number | null) ?? undefined,
    moonPhase: (row['moon_phase'] as string | null) ?? undefined,
    moonIllumination: (row['moon_illumination'] as number | null) ?? undefined,
    barometricPressure: (row['barometric_pressure'] as number | null) ?? undefined,
    barometricTrend: (row['barometric_trend'] as LogEntry['barometricTrend']) ?? undefined,
    weatherFeelsLike: (row['weather_feels_like'] as number | null) ?? undefined,
    weatherHumidity: (row['weather_humidity'] as number | null) ?? undefined,
    weatherIcon: (row['weather_icon'] as string | null) ?? undefined,
    civilDawnTime: (row['civil_dawn'] as string | null) ?? undefined,
    civilDuskTime: (row['civil_dusk'] as string | null) ?? undefined,
    sunriseTime: (row['sun_rise'] as string | null) ?? undefined,
    sunsetTime: (row['sun_set'] as string | null) ?? undefined,
    animalObserved: (row['animal_observed'] as boolean) ?? false,
    animalSpecies: (row['animal_species'] as string | null) ?? undefined,
    animalSex: (row['animal_sex'] as LogEntry['animalSex']) ?? undefined,
    animalAgeClass: (row['animal_age_class'] as string | null) ?? undefined,
    animalAntlerDescription: (row['animal_antler_description'] as string | null) ?? undefined,
    animalCount: (row['animal_count'] as number | null) ?? undefined,
    animalBehavior: (row['animal_behavior'] as string | null) ?? undefined,
    photos: (row['photos'] as string[]) ?? [],
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertLogEntry(entry: LogEntry): Promise<void> {
  const { error } = await supabase
    .from('log_entries')
    .upsert(toSupabaseLogEntry(entry), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertLogEntry');
}

export async function fetchLogEntriesSince(since: string): Promise<LogEntry[]> {
  const { data, error } = await supabase
    .from('log_entries')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchLogEntriesSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseLogEntry);
}

export async function softDeleteLogEntry(id: string): Promise<void> {
  const { error } = await supabase
    .from('log_entries')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteLogEntry');
}

// ---------------------------------------------------------------------------
// Shopping List Items (Phase 3 — schema ready, implementation in M33)
// ---------------------------------------------------------------------------

function toSupabaseShoppingListItem(item: ShoppingListItem): Record<string, unknown> {
  return {
    id: item.id,
    user_id: item.userId,
    task_id: item.taskId ?? null,
    property_id: item.propertyId ?? null,
    item: item.name,
    category: item.category ?? 'other',
    quantity: item.quantity != null ? String(item.quantity) : '',
    purchased: item.purchased,
    purchased_at: item.purchasedAt ?? null,
    notes: item.notes ?? '',
    created_at: item.createdAt,
    updated_at: item.updatedAt,
  };
}

function fromSupabaseShoppingListItem(row: Record<string, unknown>): ShoppingListItem {
  const quantityStr = (row['quantity'] as string) ?? '';
  const quantity = quantityStr ? parseFloat(quantityStr) : undefined;
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    taskId: (row['task_id'] as string | null) ?? undefined,
    propertyId: (row['property_id'] as string | null) ?? undefined,
    name: row['item'] as string,
    category: (row['category'] as string) ?? 'other',
    quantity: isNaN(quantity as number) ? undefined : quantity,
    purchased: (row['purchased'] as boolean) ?? false,
    purchasedAt: (row['purchased_at'] as string | null) ?? undefined,
    notes: (row['notes'] as string) ?? '',
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export async function upsertShoppingListItem(item: ShoppingListItem): Promise<void> {
  const { error } = await supabase
    .from('shopping_list')
    .upsert(toSupabaseShoppingListItem(item), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertShoppingListItem');
}

export async function fetchShoppingListSince(since: string): Promise<ShoppingListItem[]> {
  const { data, error } = await supabase
    .from('shopping_list')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchShoppingListSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseShoppingListItem);
}

export async function softDeleteShoppingListItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('shopping_list')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteShoppingListItem');
}

// ---------------------------------------------------------------------------
// Hunts (M48)
// ---------------------------------------------------------------------------

function toSupabaseHunt(h: Hunt): Record<string, unknown> {
  return {
    id: h.id,
    user_id: h.userId,
    season_id: h.seasonId,
    property_id: h.propertyId,
    name: h.name,
    start_date: h.startDate,
    end_date: h.endDate,
    companions: h.companions,
    photos: h.photos,
    created_at: h.createdAt,
    updated_at: h.updatedAt,
    synced_at: h.syncedAt ?? null,
    deleted_at: h.deletedAt ?? null,
  };
}

function fromSupabaseHunt(row: Record<string, unknown>): Hunt {
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    seasonId: row['season_id'] as string,
    propertyId: row['property_id'] as string,
    name: row['name'] as string,
    startDate: row['start_date'] as string,
    endDate: row['end_date'] as string,
    companions: (row['companions'] as string[]) ?? [],
    photos: (row['photos'] as string[]) ?? [],
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    syncedAt: (row['synced_at'] as string | null) ?? undefined,
    deletedAt: (row['deleted_at'] as string | null) ?? undefined,
  };
}

export async function upsertHunt(hunt: Hunt): Promise<void> {
  const { error } = await supabase.from('hunts').upsert(toSupabaseHunt(hunt), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertHunt');
}

export async function deleteHunt(id: string): Promise<void> {
  const { error } = await supabase.from('hunts').delete().eq('id', id);
  if (error) throwOnError(error, 'deleteHunt');
}

export async function softDeleteHunt(id: string): Promise<void> {
  const { error } = await supabase
    .from('hunts')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteHunt');
}

export async function fetchHuntsSince(since: string): Promise<Hunt[]> {
  const { data, error } = await supabase
    .from('hunts')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchHuntsSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseHunt);
}

// ---------------------------------------------------------------------------
// HuntSits (M48)
// Column reference: supabase/migrations/0005_hunt_schema.sql — column is `location_id` (not `waypoint_id`)
// ---------------------------------------------------------------------------

function toSupabaseHuntSit(s: HuntSit): Record<string, unknown> {
  return {
    id: s.id,
    hunt_id: s.huntId,
    location_id: s.waypointId,
    user_id: s.userId,
    date: s.date,
    period: s.period,
    time_in: s.timeIn ?? null,
    time_out: s.timeOut ?? null,
    conditions: s.conditions ?? null,
    observations: s.observations,
    notes: s.notes ?? null,
    created_at: s.createdAt,
    updated_at: s.updatedAt,
    synced_at: s.syncedAt ?? null,
    deleted_at: s.deletedAt ?? null,
  };
}

function fromSupabaseHuntSit(row: Record<string, unknown>): HuntSit {
  return {
    id: row['id'] as string,
    huntId: row['hunt_id'] as string,
    waypointId: row['location_id'] as string,
    userId: row['user_id'] as string,
    date: row['date'] as string,
    period: row['period'] as HuntSit['period'],
    timeIn: (row['time_in'] as string | null) ?? undefined,
    timeOut: (row['time_out'] as string | null) ?? undefined,
    // CAST: JSONB from Postgres is safe to cast to ConditionsSnapshot
    conditions: (row['conditions'] as HuntSit['conditions']) ?? undefined,
    // CAST: JSONB from Postgres is safe to cast to AnimalObservation[]
    observations: (row['observations'] as HuntSit['observations']) ?? [],
    notes: (row['notes'] as string | null) ?? undefined,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    syncedAt: (row['synced_at'] as string | null) ?? undefined,
    deletedAt: (row['deleted_at'] as string | null) ?? undefined,
  };
}

export async function upsertHuntSit(sit: HuntSit): Promise<void> {
  const { error } = await supabase.from('hunt_sits').upsert(toSupabaseHuntSit(sit), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertHuntSit');
}

export async function deleteHuntSit(id: string): Promise<void> {
  const { error } = await supabase.from('hunt_sits').delete().eq('id', id);
  if (error) throwOnError(error, 'deleteHuntSit');
}

export async function softDeleteHuntSit(id: string): Promise<void> {
  const { error } = await supabase
    .from('hunt_sits')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteHuntSit');
}

export async function fetchHuntSitsSince(since: string): Promise<HuntSit[]> {
  const { data, error } = await supabase
    .from('hunt_sits')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchHuntSitsSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseHuntSit);
}

// ---------------------------------------------------------------------------
// HarvestRecords (M48)
// Column reference: supabase/migrations/0005_hunt_schema.sql — column is `location_id` (not `waypoint_id`)
// ---------------------------------------------------------------------------

function toSupabaseHarvestRecord(r: HarvestRecord): Record<string, unknown> {
  return {
    id: r.id,
    hunt_id: r.huntId,
    hunt_sit_id: r.huntSitId ?? null,
    location_id: r.waypointId,
    user_id: r.userId,
    date: r.date,
    species: r.species,
    sex: r.sex,
    weight: r.weight ?? null,
    antler_points: r.antlerPoints ?? null,
    dressed_weight: r.dressedWeight ?? null,
    notes: r.notes ?? null,
    photos: r.photos,
    thumbnail_photo_id: r.thumbnailPhotoId ?? null,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    synced_at: r.syncedAt ?? null,
    deleted_at: r.deletedAt ?? null,
  };
}

function fromSupabaseHarvestRecord(row: Record<string, unknown>): HarvestRecord {
  return {
    id: row['id'] as string,
    huntId: row['hunt_id'] as string,
    huntSitId: (row['hunt_sit_id'] as string | null) ?? undefined,
    waypointId: row['location_id'] as string,
    userId: row['user_id'] as string,
    date: row['date'] as string,
    species: row['species'] as string,
    sex: row['sex'] as HarvestRecord['sex'],
    weight: (row['weight'] as number | null) ?? undefined,
    antlerPoints: (row['antler_points'] as number | null) ?? undefined,
    dressedWeight: (row['dressed_weight'] as number | null) ?? undefined,
    notes: (row['notes'] as string | null) ?? undefined,
    photos: (row['photos'] as string[]) ?? [],
    thumbnailPhotoId: (row['thumbnail_photo_id'] as string | null) ?? undefined,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    syncedAt: (row['synced_at'] as string | null) ?? undefined,
    deletedAt: (row['deleted_at'] as string | null) ?? undefined,
  };
}

export async function upsertHarvestRecord(record: HarvestRecord): Promise<void> {
  const { error } = await supabase
    .from('harvest_records')
    .upsert(toSupabaseHarvestRecord(record), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertHarvestRecord');
}

export async function deleteHarvestRecord(id: string): Promise<void> {
  const { error } = await supabase.from('harvest_records').delete().eq('id', id);
  if (error) throwOnError(error, 'deleteHarvestRecord');
}

export async function softDeleteHarvestRecord(id: string): Promise<void> {
  const { error } = await supabase
    .from('harvest_records')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteHarvestRecord');
}

export async function fetchHarvestRecordsSince(since: string): Promise<HarvestRecord[]> {
  const { data, error } = await supabase
    .from('harvest_records')
    .select('*')
    .is('deleted_at', null)
    .gt('updated_at', since);
  if (error) throwOnError(error, 'fetchHarvestRecordsSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseHarvestRecord);
}

// ---------------------------------------------------------------------------
// HuntJournalEntries (M80)
// ---------------------------------------------------------------------------

function toSupabaseHuntJournalEntry(e: HuntJournalEntry): Record<string, unknown> {
  return {
    id: e.id,
    hunt_id: e.huntId,
    user_id: e.userId,
    body: e.body,
    tag: e.tag ?? null,
    location_id: e.locationId ?? null,
    hunt_sit_id: e.huntSitId ?? null,
    visibility: e.visibility,
    created_at: e.createdAt,
    updated_at: e.updatedAt,
    synced_at: e.syncedAt ?? null,
    deleted_at: e.deletedAt ?? null,
  };
}

function fromSupabaseHuntJournalEntry(row: Record<string, unknown>): HuntJournalEntry {
  return {
    id: row['id'] as string,
    huntId: row['hunt_id'] as string,
    userId: row['user_id'] as string,
    body: row['body'] as string,
    tag: (row['tag'] as HuntJournalEntry['tag'] | null) ?? undefined,
    locationId: (row['location_id'] as string | null) ?? undefined,
    huntSitId: (row['hunt_sit_id'] as string | null) ?? undefined,
    visibility: row['visibility'] as HuntJournalEntry['visibility'],
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    syncedAt: (row['synced_at'] as string | null) ?? undefined,
    deletedAt: (row['deleted_at'] as string | null) ?? undefined,
  };
}

export async function upsertHuntJournalEntry(entry: HuntJournalEntry): Promise<void> {
  const { error } = await supabase
    .from('hunt_journal_entries')
    .upsert(toSupabaseHuntJournalEntry(entry), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertHuntJournalEntry');
}

export async function softDeleteHuntJournalEntry(id: string): Promise<void> {
  const { error } = await supabase
    .from('hunt_journal_entries')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throwOnError(error, 'softDeleteHuntJournalEntry');
}

export async function fetchHuntJournalEntriesSince(since: string): Promise<HuntJournalEntry[]> {
  const { data, error } = await supabase
    .from('hunt_journal_entries')
    .select('*')
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'fetchHuntJournalEntriesSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabaseHuntJournalEntry);
}

// ---------------------------------------------------------------------------
// Photos (M81 — metadata sync; blob never crosses the network)
// ---------------------------------------------------------------------------

/**
 * Map a PhotoRecord to a Supabase row, explicitly excluding the blob field.
 * The blob lives in Supabase Storage; Postgres only holds metadata + remoteUrl.
 */
function toSupabasePhoto(photo: PhotoRecord): Record<string, unknown> {
  return {
    id: photo.id,
    user_id: photo.userId,
    log_entry_id: photo.logEntryId ?? null,
    hunt_id: photo.huntId ?? null,
    sit_id: photo.sitId ?? null,
    harvest_id: photo.harvestId ?? null,
    mime_type: photo.mimeType,
    width: photo.width,
    height: photo.height,
    size_bytes: photo.sizeBytes,
    remote_url: photo.remoteUrl ?? null,
    created_at: photo.createdAt,
    updated_at: photo.updatedAt ?? photo.createdAt,
    // blob intentionally excluded — bytes live in Supabase Storage only
  };
}

/**
 * Map a Supabase row back to a PhotoRecord.
 * blobBytes/blobType are always undefined — callers must use downloadPhoto() to hydrate bytes.
 * blobSize is populated from the Postgres size_bytes column for parity with local-written rows.
 */
function fromSupabasePhoto(row: Record<string, unknown>): PhotoRecord {
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    logEntryId: (row['log_entry_id'] as string | null) ?? undefined,
    huntId: (row['hunt_id'] as string | null) ?? undefined,
    sitId: (row['sit_id'] as string | null) ?? undefined,
    harvestId: (row['harvest_id'] as string | null) ?? undefined,
    // Byte fields intentionally absent on pulled rows — bytes are never stored in Postgres.
    // downloadPhoto() writes them back after fetching from Supabase Storage.
    blobBytes: undefined,
    blobType: undefined,
    blobSize: row['size_bytes'] as number,
    mimeType: row['mime_type'] as string,
    width: row['width'] as number,
    height: row['height'] as number,
    sizeBytes: row['size_bytes'] as number,
    remoteUrl: (row['remote_url'] as string | null) ?? undefined,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

/**
 * Upsert a PhotoRecord into Postgres. Blob is never included.
 * Idempotent: repeat calls with the same id are safe (uses onConflict: 'id').
 */
export async function upsertPhoto(photo: PhotoRecord): Promise<void> {
  const { error } = await supabase
    .from('photos')
    .upsert(toSupabasePhoto(photo), { onConflict: 'id' });
  if (error) throwOnError(error, 'upsertPhoto');
}

/**
 * Fetch photo rows updated after `since` for the given user.
 * Returns PhotoRecord objects with blobBytes/blobType undefined on every row.
 * blobSize is populated from size_bytes for parity with local-written rows.
 */
export async function getPhotosSince(userId: string, since: string): Promise<PhotoRecord[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .eq('user_id', userId)
    .gt('updated_at', since)
    .is('deleted_at', null);
  if (error) throwOnError(error, 'getPhotosSince');
  return ((data ?? []) as Record<string, unknown>[]).map(fromSupabasePhoto);
}

/**
 * Soft-delete a photo row by setting deleted_at = now().
 * The corresponding Supabase Storage object is handled separately by deleteRemotePhoto().
 */
export async function softDeletePhoto(photoId: string): Promise<void> {
  const { error } = await supabase
    .from('photos')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', photoId);
  if (error) throwOnError(error, 'softDeletePhoto');
}

// ---------------------------------------------------------------------------
// Soft-delete dispatch (used by sync-service for pendingDeletes queue)
// ---------------------------------------------------------------------------

export async function softDeleteByEntityType(
  id: string,
  entityType: string,
  userId?: string,
): Promise<void> {
  switch (entityType) {
    case 'user':              return softDeleteUser(id);
    case 'userPreferences':   return softDeleteUserPreferences(id);
    case 'property':          return softDeleteProperty(id);
    case 'waypoint':          return softDeleteWaypoint(id);
    case 'season':            return softDeleteSeason(id);
    case 'task':              return softDeleteTask(id);
    case 'logEntry':          return softDeleteLogEntry(id);
    case 'shoppingListItem':  return softDeleteShoppingListItem(id);
    case 'photo':
      // 'photo' (singular) = Storage bucket delete path, handled by photo-sync-service — skip here
      return;
    case 'photos':
      // 'photos' (plural) = Postgres metadata row soft-delete (M81)
      return softDeletePhoto(id);
    case 'hunt':              return softDeleteHunt(id);
    case 'huntSit':           return softDeleteHuntSit(id);
    case 'harvestRecord':     return softDeleteHarvestRecord(id);
    case 'huntJournalEntry':  return softDeleteHuntJournalEntry(id);
    default:
      console.error(`[supabase-repository] Unknown entityType for softDelete: ${entityType}`);
  }
  void userId; // userId reserved for future entity types that need it
}
